import React from 'react';
import { US_STATES, normalizeStateToCode } from '../utils/usStates';

/**
 * StateSelect — the ONE US-state control. MIRROR of the same component in
 * apps/contractor-app/src/components/Common/ and apps/customer-portal/src/components/Common/
 * (see utils/usStates.js in this package for why all three are copies).
 *
 * WHY THIS EXISTS
 * A swept audit on 2026-08-23 found 19 free-text `<input name="state">` fields across the
 * two apps. Free text is not a style problem — it is how the stored data got broken.
 * This copy closes the LAST one: BookingForm's location_state, which is what put "ORegon"
 * in front of the operator on the portal's Request Service page (verified 2026-08-24 —
 * customers.billing_state held the typo, CustomerContext maps it to customer.state,
 * RequestService prefills it, and this form accepted it verbatim).
 *
 * THE EVENT CONTRACT IS DELIBERATE
 * onChange receives the raw change EVENT, exactly like the `<input>` this replaces, so a
 * caller already doing `onChange={handleChange}` with a `name` needs no other change.
 *
 * The displayed value is normalised on the way in, so a legacy row holding "Oregon" or
 * "ORegon" still selects OR instead of showing blank and silently discarding itself when
 * the user saves an unrelated field.
 *
 * Do NOT hardcode another state array. Extend utils/usStates.js — and keep the two app
 * mirrors in step.
 */
const StateSelect = ({
  value,
  onChange,
  name = 'state',
  id,
  className = '',
  disabled = false,
  required = false,
  placeholder = 'Select state',
  // Classes for the <option> rows. Native select popups are drawn by the OS and ignore
  // the app's theme, so a dark form gets a white popup unless each option carries its own
  // background and colour (same fix as the onboarding wizard, d238c33c9).
  //
  // OPT-IN with an empty default, deliberately: this component is in shared-ui and is
  // rendered by the customer portal as well as the contractor app, so hardcoding
  // `bg-slate-800` here would repaint someone else's light form. Dark callers pass it.
  optionClassName = '',
  // Everything else (aria-label, data-testid, onBlur, …) passes straight through, so
  // this is a genuine drop-in for the <input> it replaces.
  ...rest
}) => {
  const current = normalizeStateToCode(value) || '';
  const isKnown = US_STATES.some((s) => s.code === current);

  return (
    <select
      name={name}
      id={id}
      value={current}
      onChange={onChange}
      disabled={disabled}
      required={required}
      className={className}
      {...rest}
    >
      <option className={optionClassName} value="">{placeholder}</option>
      {/*
        A value we do not recognise gets its own option so it DISPLAYS and round-trips.
        This is required, not a nicety: TODO.md:586 records the standing decision that we
        standardise on 2-letter USPS codes but "international passes through" — live rows
        hold "NSW" and "Greater London". normalizeStateToCode returns unknown input
        unchanged, so without this option the select would render blank, hiding the
        customer's real value and replacing it the moment anyone touched the field.
        Deliberately not offered as a choice — it only appears when already stored.
      */}
      {!isKnown && current && <option className={optionClassName} value={current}>{current}</option>}
      {US_STATES.map((s) => (
        <option className={optionClassName} key={s.code} value={s.code}>
          {s.code} — {s.name}
        </option>
      ))}
    </select>
  );
};

export default StateSelect;
